import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '../../Components/Navigation';
import '../../Assets/Styles/EditeBlog_page.css';

const EditeBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        setError(null);
        setApiError(null);
        
        const response = await fetch(`https://tstock.trillionstechsolutions.com/blogs-edits/${id}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Blog post not found. It may have been deleted or the ID is incorrect.');
          } else if (response.status === 500) {
            throw new Error('Server error. Please try again later or contact support.');
          } else {
            throw new Error(`Failed to fetch blog data (Status: ${response.status})`);
          }
        }
        
        const result = await response.json();
        
        if (result.success) {
          // Convert tags string to array for the form
          const blogData = {
            ...result.data,
            tags: result.data.tags ? result.data.tags.split(',').map(tag => tag.trim()) : []
          };
          setBlog(blogData);
          
          // Set preview image if featuredImage exists
          if (blogData.featuredImage) {
            setPreviewImage(blogData.featuredImage);
          }
        } else {
          throw new Error(result.error || 'Failed to fetch blog data');
        }
      } catch (error) {
        console.error('Error fetching blog:', error);
        setError('Failed to load blog data. Please try again later.');
        setApiError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [id]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // For preview purposes
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
      
      // Store the file for later upload
      setImageFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveStatus(null);
    setApiError(null);

    try {
      let imageUrl = blog.featuredImage;
      
      // If a new image was selected, upload it first
      if (imageFile) {
        const imageFormData = new FormData();
        imageFormData.append('image', imageFile);
        
        try {
          // Upload image first
          const imageResponse = await fetch('https://tstock.trillionstechsolutions.com/blogs-edits/upload-image', {
            method: 'POST',
            body: imageFormData
          });
          
          if (!imageResponse.ok) {
            if (imageResponse.status === 413) {
              throw new Error('Image file is too large. Please choose a smaller image.');
            } else if (imageResponse.status === 415) {
              throw new Error('Unsupported file type. Please upload a valid image file (JPG, PNG, etc.).');
            } else {
              const errorData = await imageResponse.json();
              throw new Error(errorData.error || `Failed to upload image (Status: ${imageResponse.status})`);
            }
          }
          
          const imageData = await imageResponse.json();
          imageUrl = imageData.imageUrl; // Get the URL from the response
        } catch (imageError) {
          throw new Error(`Image upload failed: ${imageError.message}`);
        }
      }
      
      // Convert tags array back to string for the API
      const blogDataToSend = {
        ...blog,
        tags: blog.tags.join(', '),
        featuredImage: imageUrl
      };

      const response = await fetch(`https://tstock.trillionstechsolutions.com/blogs-edits/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(blogDataToSend),
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Blog post not found. It may have been deleted.');
        } else if (response.status === 400) {
          const errorData = await response.json();
          throw new Error(`Validation error: ${errorData.error || 'Please check your input and try again.'}`);
        } else if (response.status === 500) {
          throw new Error('Server error. Please try again later or contact support.');
        } else {
          throw new Error(`Failed to update blog (Status: ${response.status})`);
        }
      }

      const result = await response.json();
      
      if (result.success) {
        setSaveStatus('success');
        setTimeout(() => {
          navigate('/blogs');
        }, 1500);
      } else {
        throw new Error(result.error || 'Failed to update blog');
      }
    } catch (error) {
      console.error('Error saving blog:', error);
      setSaveStatus('error');
      setApiError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="edit-blog-container">
        <Navigation />
        <div className="loader-container">
          <div className="loader">
            <div className="loader-circle"></div>
            <div className="loader-circle"></div>
            <div className="loader-circle"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="edit-blog-container">
        <Navigation />
        <div className="edit-blog-content" style={{marginTop: '60px'}}>
          <div className="error-container">
            <h2>Unable to Load Blog</h2>
            <div className="error-message">{error}</div>
            {apiError && (
              <div className="error-details">
                <p><strong>Technical Details:</strong> {apiError}</p>
                <p>If this issue persists, please contact our support team.</p>
              </div>
            )}
            <div className="error-actions">
              <button className="retry-btn" onClick={() => window.location.reload()}>
                Try Again
              </button>
              <button className="back-btn" onClick={handleCancel}>
                Back to Admin
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="edit-blog-container">
        <Navigation />
        <div className="edit-blog-content" style={{marginTop: '60px'}}>
          <div className="error-container">
            <h2>Blog Not Found</h2>
            <div className="error-message">
              The blog post you're trying to edit doesn't exist or has been removed.
            </div>
            <div className="error-actions">
              <button className="back-btn" onClick={handleCancel}>
                Back to Admin
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-blog-container">
      <Navigation />
      <div className="edit-blog-content" style={{marginTop: '60px'}}>
        <div className="edit-blog-header">
          <h1>Edit Blog Post</h1>
          <span className="blog-id">ID: {id}</span>
        </div>

        <form className="edit-blog-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>Basic Information</h2>
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                value={blog.title}
                onChange={(e) => setBlog({ ...blog, title: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="author">Author</label>
              <input
                type="text"
                id="author"
                value={blog.author}
                onChange={(e) => setBlog({ ...blog, author: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Category</label>
              <input
                type="text"
                id="category"
                value={blog.category}
                onChange={(e) => setBlog({ ...blog, category: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={blog.status}
                onChange={(e) => setBlog({ ...blog, status: e.target.value })}
                required
              >
                <option value="Draft">Draft</option>
                <option value="Published">Published</option>
              </select>
            </div>
          </div>

          <div className="form-section">
            <h2>Content</h2>
            <div className="form-group">
              <label htmlFor="content">Content</label>
              <textarea
                id="content"
                value={blog.content}
                onChange={(e) => setBlog({ ...blog, content: e.target.value })}
                required
                rows="10"
              />
            </div>

            <div className="form-group">
              <label htmlFor="excerpt">Excerpt</label>
              <textarea
                id="excerpt"
                value={blog.excerpt}
                onChange={(e) => setBlog({ ...blog, excerpt: e.target.value })}
                required
                rows="3"
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Media & SEO</h2>
            <div className="form-group">
              <label htmlFor="featuredImage">Featured Image</label>
              <div className="image-upload-container">
                <input
                  type="file"
                  id="featuredImage"
                  onChange={handleImageChange}
                  accept="image/*"
                  className="image-input"
                />
                <div className="image-preview">
                  {previewImage ? (
                    <img src={previewImage} alt="Preview" />
                  ) : (
                    <div className="image-placeholder">
                      <span>No image selected</span>
                      <span className="upload-icon">📷</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="tags">Tags (comma-separated)</label>
              <input
                type="text"
                id="tags"
                value={blog.tags.join(', ')}
                onChange={(e) => setBlog({ ...blog, tags: e.target.value.split(',').map(tag => tag.trim()) })}
              />
            </div>

            <div className="form-group">
              <label htmlFor="seoTitle">SEO Title</label>
              <input
                type="text"
                id="seoTitle"
                value={blog.seoTitle}
                onChange={(e) => setBlog({ ...blog, seoTitle: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label htmlFor="seoDescription">SEO Description</label>
              <textarea
                id="seoDescription"
                value={blog.seoDescription}
                onChange={(e) => setBlog({ ...blog, seoDescription: e.target.value })}
                rows="3"
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
            <button type="submit" className="save-btn" disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>

          {saveStatus === 'success' && (
            <div className="save-status success">
              Changes saved successfully! Redirecting...
            </div>
          )}
          {saveStatus === 'error' && (
            <div className="save-status error">
              <h3>Error Saving Changes</h3>
              <p>{apiError || 'An unexpected error occurred. Please try again.'}</p>
              <button className="retry-btn" onClick={() => setSaveStatus(null)}>
                Try Again
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default EditeBlog;
